import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';

interface DateRangePanelProps {
  /** Main content: an interactive picker or a static date display. */
  children: ReactNode;
  /** Optional subtitle rendered below the main content in muted small text. */
  subtitle?: ReactNode;
  /** The opening's zone beside the caption: the abbreviation, with its full name on hover or focus. */
  timeZoneLabel?: { short: string; long: string } | null;
}

export function DateRangePanel({
  children,
  subtitle,
  timeZoneLabel,
}: DateRangePanelProps) {
  return (
    <div className="flex w-[240px] shrink-0 flex-col items-start gap-2">
      <div className="flex items-baseline gap-1">
        <Typography variant="caption">
          <FormattedMessage
            defaultMessage="Date range"
            description="Caption above a date range display on opening details"
          />
        </Typography>
        {timeZoneLabel && (
          <Tooltip
            content={
              <FormattedMessage
                defaultMessage="All dates are in the opening's time zone: {timeZone}"
                description="Tooltip on the time zone beside the opening's date range. {timeZone} is a zone name such as Pacific Daylight Time (PDT)"
                values={{ timeZone: timeZoneLabel.long }}
              />
            }
          >
            <button
              type="button"
              className="cursor-help rounded-sm"
              data-testid="date-range-time-zone"
            >
              <Typography variant="small1" className="text-fx-neutral-600">
                ({timeZoneLabel.short})
              </Typography>
            </button>
          </Tooltip>
        )}
      </div>
      {children}
      {subtitle && (
        <Typography variant="small1" className="text-fx-neutral-600">
          {subtitle}
        </Typography>
      )}
    </div>
  );
}

DateRangePanel.Skeleton = function DateRangePanelSkeleton() {
  return (
    <div className="flex w-[240px] shrink-0 flex-col items-start gap-2">
      <div className="bg-fx-neutral-200 h-3 w-20 rounded" />
      <div className="bg-fx-neutral-200 h-5 w-52 rounded" />
      <div className="bg-fx-neutral-200 h-4 w-24 rounded" />
    </div>
  );
};
